import React from 'react'
import { useQuery } from '@tanstack/react-query'
import type { KambiBetOffer } from '@mobi/api-types/kambi'
import { BetOfferType } from '@mobi/api-types/kambi/constants'
import { getKambiBetOffersByIdsQuery } from '@mobi/sports-offering/helpers/queries'
import { TeamIcon } from './Components/TeamIcon'

export const TeamsIcon: React.FC<
  {
    outcomeId: number
    betOfferType: BetOfferType
    betOfferId: KambiBetOffer['id']
  } & TeamsIconProps
> = ({
  betOfferId,
  betOfferType,
  outcomeId,
  size,
  sportName,
  participantId,
  eventId,
  defaultIconType = 'sport-icon',
}) => {
  if (
    [
      BetOfferType.PlayerLastOccurrence,
      BetOfferType.PlayerOccurrenceLine,
      BetOfferType.PlayerOccurrenceNumber,
    ].includes(betOfferType)
  ) {
    return (
      <TeamsIconWithPlayerSupport
        betOfferId={betOfferId}
        outcomeId={outcomeId}
        size={size}
        sportName={sportName}
        participantId={participantId}
        defaultIconType={defaultIconType}
        eventId={eventId}
      />
    )
  }

  return (
    <TeamIcon
      size={size}
      sportName={sportName}
      participantId={participantId}
      defaultIconType={defaultIconType}
      eventId={eventId}
    />
  )
}

// ================
// Local Components
// ================

/** For individuals on team, fetch team participant id */
const TeamsIconWithPlayerSupport: React.FC<
  {
    betOfferId: KambiBetOffer['id']
    outcomeId: number
  } & TeamsIconProps
> = ({ betOfferId, outcomeId, size, sportName, defaultIconType, eventId }) => {
  const { data, isLoading, isError } = useQuery(getKambiBetOffersByIdsQuery([betOfferId]))

  if (isLoading || (!data && !isError)) return null

  const outcome = data?.betOffers[0]?.outcomes.find(o => o.id === outcomeId)
  const participantId = outcome?.eventParticipantId ?? outcome?.participantId

  return (
    <TeamIcon
      size={size}
      sportName={sportName}
      participantId={participantId}
      defaultIconType={defaultIconType}
      eventId={eventId}
    />
  )
}

// ===========
// Local Types
// ===========

type TeamsIconProps = React.ComponentProps<typeof TeamIcon>
